// FloorLanguageControl — shared Clean/Moderate/Unfiltered selector for the AI
// trading floor. Reads & writes the SINGLE global backend setting
// (/api/floor-chat/settings) so desktop + mobile + the live floor stay in sync.
import { useState, useEffect, useCallback } from "react";
import { MessageSquareText } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Auth is cookie-based; this supplies the CSRF header the backend requires
// on mutating requests (see App.js's getCsrfToken for the same pattern).
// Frontend/backend are different origins in production, so document.cookie
// can't see the csrf_token cookie the backend sets — login/auth-me responses
// hand it back in the JSON body instead, stashed in localStorage under this
// same key by App.js. Fall back to the cookie for same-origin setups.
function authHeaders() {
  try {
    const stored = localStorage.getItem("roi_csrf_token");
    if (stored) return { "X-CSRF-Token": stored };
  } catch {}
  try {
    const m = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/);
    return m ? { "X-CSRF-Token": decodeURIComponent(m[1]) } : {};
  } catch { return {}; }
}

const LEVELS = [
  { id: "clean", label: "Clean", desc: "No profanity" },
  { id: "moderate", label: "Moderate", desc: "Salty when heated" },
  { id: "unfiltered", label: "Unfiltered", desc: "Real pit talk" },
];

export function FloorLanguageControl({ accent = "#0A84FF" }) {
  const [mode, setMode] = useState("moderate");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch(`${API}/floor-chat/settings`, { credentials: "include", headers: authHeaders() })
      .then((r) => r.ok ? r.json() : null)
      .then((d) => { if (d?.profanity_mode) setMode(d.profanity_mode); })
      .catch(() => {});
  }, []);

  const change = useCallback(async (m) => {
    setMode(m);
    setSaving(true);
    try {
      await fetch(`${API}/floor-chat/settings`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ profanity_mode: m }),
      });
    } catch {}
    setTimeout(() => setSaving(false), 600);
  }, []);

  return (
    <div data-testid="floor-language-control">
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <MessageSquareText size={15} color={accent} />
        <span style={{ fontSize: 13, fontWeight: 700, color: "#e6edf3" }}>Floor Language</span>
        {saving && <span style={{ fontSize: 9, color: "#4ade80", marginLeft: "auto" }}>✓ SAVED</span>}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
        {LEVELS.map((l) => {
          const on = mode === l.id;
          return (
            <button key={l.id} type="button" onClick={() => change(l.id)} data-testid={`floor-lang-${l.id}`}
              style={{
                padding: "12px 6px", borderRadius: 12, cursor: "pointer", textAlign: "center",
                background: on ? `${accent}22` : "rgba(255,255,255,0.03)",
                border: `1.5px solid ${on ? accent : "rgba(255,255,255,0.08)"}`,
                color: on ? accent : "#e6edf3", transition: "background .15s, border-color .15s, color .15s",
              }}>
              <div style={{ fontSize: 12.5, fontWeight: 700 }}>{l.label}</div>
              <div style={{ fontSize: 9, color: "#8b949e", marginTop: 2 }}>{l.desc}</div>
            </button>
          );
        })}
      </div>
      <div style={{ fontSize: 10, color: "#636e7b", marginTop: 8, lineHeight: 1.5 }}>
        How salty the AI agents get. Applies to the whole floor — desktop and mobile pull from the same engine. Hot-headed traders swear when heated; leaders stay composed.
      </div>
    </div>
  );
}
